import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import getConfig from 'next/config';
import { handleClientUnauthorizedResponse } from '../../services/session';
import fetch from '../../lib/fetch';
import isBrowser from '../../util/isBrowser';
import { captureExceptionWithState } from '../../util/sentry';

const { WEB_URI } = getConfig().publicRuntimeConfig;

/**
 * `AudioContext`
 *
 * This context is used for managing browser resources related to audio playback. Currently, we have
 * a 1:1 mapping of audio src elements to HTMLAudioElement. The underlying src is also created via
 * the URL.createObjectURL method and must be included in the resource management.
 * In the future, we should consider using lower level libraries for finer control over audio
 * resources and playback.
 *
 */
interface ContextState {
  // unique identifier - either the src url or a unique id
  [key: string]: [
    // actual audio element
    HTMLAudioElement | Error | undefined,
    // local objectUrl for downloaded audio
    string | null | undefined
  ];
}

type ContextActions = {
  createAudioElement: ({ src, headers, id }: FetchClipProps) => void;
  disposeAudioElementIfPaused: (src: string, id?: string) => void;
  pauseAudioElementBySrc: (src: string) => void;
  setGlobalAudioElement: ({
    shouldResetIfPaused,
    src,
    id,
  }: {
    shouldResetIfPaused: boolean;
    src: string;
    id?: string;
  }) => void;
};

// kept this as an explicit headers reference vs generic for now - since this is the only headers
// we are supporting currently
type StudioAudioHeaders = {
  Accept: 'audio/*';
  'X-WellSaid-Audio-Tag': '1';
};

export type FetchClipProps = {
  src: string;
  /**
   * note null is allowed here vs undefined to conditionally set the headers object
   * to be undefined while also allowing for default `StudioAudioHeaders`
   */
  headers?: StudioAudioHeaders | null;
  id?: string;
};

type ContextProps = [ContextState, ContextActions];

const getInitialState = (): ContextState => ({});

export const AudioContext = createContext<ContextProps>([
  getInitialState(),
  {
    createAudioElement: () => {},
    disposeAudioElementIfPaused: () => {},
    pauseAudioElementBySrc: () => {},
    setGlobalAudioElement: () => {},
  },
]);
AudioContext.displayName = 'AudioContext';

export function useAudioContext(): ContextProps {
  return useContext(AudioContext);
}

export const AudioProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<ContextState>(getInitialState());
  const router = useRouter();
  const routerPath = router ? router.asPath : '';

  // The next two types have `undefined` in the definition to account for a missing `key`,
  // when accessing the map.
  const abortControllers = useRef<{
    [key: string]: AbortController | null | undefined;
  }>({});
  const pauseTimes = useRef<{ [key: string]: number | undefined }>({});

  const fetchClip = useCallback(
    async ({
      src,
      headers = {
        Accept: 'audio/*',
        'X-WellSaid-Audio-Tag': '1',
      },
      id,
    }: FetchClipProps) => {
      let responseStatus;
      try {
        const controller = new AbortController();
        abortControllers.current = {
          ...abortControllers.current,
          [id || src]: controller,
        };
        // 1. Fetch the resource
        const response = await fetch(src, {
          headers: headers === null ? undefined : headers,
          signal: controller.signal,
        });
        abortControllers.current = {
          ...abortControllers.current,
          [id || src]: null,
        };
        responseStatus = response.status;
        if (responseStatus === 401) {
          // Just refresh the page if it's a shared project URL
          // It most likely means the project is not shared anymore
          if (router?.pathname.includes('/s/')) {
            window.location.reload();
          }

          // Redirect to the sign_in page for the unauthorized user.
          // It most likely means that the user is accessing a project tab when
          // they are not authenticated anymore
          handleClientUnauthorizedResponse({
            detectionLocation: `studio/[projectId]`,
          });
        }
        if (response.status === 404) throw new Error('Clip not found');
        if (response.status >= 500) throw new Error('Service unavailable');
        const contentType = response.headers.get('Content-Type');
        const responseBlob = await response.blob();
        // 2. Create the object url (used as src for the HTMLAudioElement)
        const objectUrl = createObjectURL(responseBlob);
        // 3. Create the Audio element
        const audio = new Audio(objectUrl);
        audio.preload = 'metadata';
        if (contentType) audio.setAttribute('type', contentType);
        setState(existingState => ({
          ...existingState,
          [id || src]: [audio, objectUrl],
        }));
      } catch (error) {
        if (!(error instanceof Error)) throw error;
        abortControllers.current = {
          ...abortControllers.current,
          [id || src]: null,
        };

        if (error.name !== 'AbortError') {
          setState(
            (existingState: ContextState) =>
              ({
                ...existingState,
                [id || src]: [error, null],
              } as ContextState)
          );
          // Track this as an Sentry error
          captureExceptionWithState(error, {
            type: 'AudioContext',
            value: {
              src,
              id,
              status: `${responseStatus}`,
            },
          });
        }
      }
    },
    [router]
  );

  const createAudioElement = useCallback(
    ({ src, headers, id }: FetchClipProps) => {
      setState(prevState => {
        const isStudioClip = src?.includes(`${WEB_URI}/api/users/clips`);

        const [audio, objectUrl] = prevState[id || src] || [];
        if (audio) return prevState;
        if (isStudioClip && !objectUrl) {
          fetchClip({ src, headers, id });
          return prevState;
        }
        const studioClipUrl = objectUrl || '';
        const audioUrl = !isStudioClip && src ? src : studioClipUrl;

        const newAudio = new Audio(audioUrl);
        // pauseTimes potentially has the time this clip was paused at before unmounting,
        // here, we set it back to that number.
        const currentTime = pauseTimes.current[id || src] || 0;
        newAudio.currentTime = currentTime;
        return {
          ...prevState,
          [id || src]: [newAudio, objectUrl], // key should be a unique identifier - either src or id
        };
      });
    },
    [fetchClip]
  );

  const setGlobalAudioElement = useCallback(
    ({
      src,
      shouldResetIfPaused,
      id,
    }: {
      shouldResetIfPaused: boolean;
      src: string;
      id?: string;
    }) => {
      setState(prevState => {
        const [audio, objectUrl] = prevState[id || src] || [];
        const audioRef = prevState[id || src];
        const globalKey = 'global';
        const globalAudioRef = prevState[globalKey];

        if (globalAudioRef) {
          const [globalAudio] = prevState[globalKey];
          if (globalAudio instanceof HTMLAudioElement) {
            if (shouldResetIfPaused) globalAudio.currentTime = 0;
            globalAudio.pause();
          }
        }
        if (audioRef) {
          const updatedState: ContextState = {
            ...prevState,
            [globalKey]: [audio, objectUrl],
          };
          return updatedState;
        }
        return prevState;
      });
    },
    []
  );

  /**
   * @todo can this be simplified? I tried using state directly but ran into
   * issue with iterable instance
   */
  const pauseAudioElementBySrc = useCallback((src: string, id?: string) => {
    setState(prevState => {
      const audioRef = prevState[id || src];
      if (audioRef) {
        const [audio] = prevState[id || src];
        if (audio instanceof HTMLAudioElement) {
          audio.pause();
        }
      }
      return prevState;
    });
  }, []);

  const disposeAudioElementIfPaused = useCallback<
    ContextActions['disposeAudioElementIfPaused']
  >((src: string, id?: string) => {
    // eslint-disable-next-line no-unused-expressions
    abortControllers.current[id || src]?.abort();
    setState(prevState => {
      const [audio, objectSrc] = prevState[id || src] || [];
      if (audio instanceof HTMLAudioElement && audio.paused) {
        try {
          // Save currentTime to set the AudioElement to it once the components mounts again
          pauseTimes.current = {
            ...pauseTimes.current,
            [src]: audio.currentTime,
          };
          // `audio.src = ''` is needed to 'release' the audio element from chrome
          // https://stackoverflow.com/questions/68480528/blocked-attempt-to-create-a-webmediaplayer-as-there-are-too-many-webmediaplayers#comment121228123_68576498
          audio.src = '';
          audio.srcObject = null;
          audio.remove();
          return {
            ...prevState,
            [id || src]: [undefined, objectSrc],
          };
        } catch (error) {
          console.warn(
            `Error while attempting to release audio element:`,
            error
          );
        }
      }
      if (audio instanceof HTMLAudioElement && !audio.paused) {
        audio.pause();

        // Save currentTime to set the AudioElement to it once the components mounts again
        pauseTimes.current = {
          ...pauseTimes.current,
          [id || src]: audio.currentTime,
        };
      }
      return prevState;
    });
  }, []);

  const cleanup = useCallback(() => {
    setState(existingState => {
      const updatedKeys = Object.keys(existingState).reduce(
        (acc, lookupKey) => {
          // lookup key is always either src or unique id
          const [audio] = existingState[lookupKey] || [];
          if (audio instanceof HTMLAudioElement) {
            try {
              audio.pause();
              audio.remove();
              audio.srcObject = null;
              revokeObjectURL(audio.src);
              return acc.concat(lookupKey);
            } catch (error) {
              console.warn(
                `Error while attempting to release audio element:`,
                error
              );
            }
          }
          return acc;
        },
        [] as string[]
      );
      return omit(existingState, updatedKeys);
    });
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Respond to router path change by pausing all audio elements
  useEffect(() => {
    // Note, by wrapping this in setState we avoid having `state` in the dependency array (which
    // would trigger this effect on every state change and router path change)
    setState(prevState => {
      Object.keys(prevState).forEach(lookupKey => {
        pauseTimes.current = {
          ...pauseTimes.current,
          [lookupKey]: 0,
        };
        const [entry] = prevState[lookupKey] || [];
        if (entry instanceof HTMLAudioElement) {
          if (!entry.paused) {
            try {
              entry.pause();
            } catch (error) {
              console.warn(
                `Error while attempting to pause audio element`,
                error
              );
            }
          }
          entry.currentTime = 0;
        }
      });
      return prevState;
    });
  }, [routerPath]);

  const audioContextValue: ContextProps = useMemo(
    () => [
      state,
      {
        createAudioElement,
        disposeAudioElementIfPaused,
        pauseAudioElementBySrc,
        setGlobalAudioElement,
      },
    ],
    [
      state,
      createAudioElement,
      disposeAudioElementIfPaused,
      pauseAudioElementBySrc,
      setGlobalAudioElement,
    ]
  );

  return (
    <AudioContext.Provider value={audioContextValue}>
      {children}
    </AudioContext.Provider>
  );
};

function createObjectURL(blob: Blob) {
  if (isBrowser && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(blob);
  }
  return undefined;
}

function revokeObjectURL(url: string) {
  if (isBrowser && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}
